import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { SectionLayout } from "@/components/SectionLayout";
import { Heading, Text } from "@/components/Text";
import { Button } from "@/components/Button";
import { getMediaCoverage } from "../../sanity/lib/getCmsData";
import { urlForImage } from "../../sanity/lib/image";

export async function getStaticProps() {
  const data = await getMediaCoverage();
  return {
    props: {
      data,
    },
    revalidate: 10,
  };
}

export default function MediaCoverage({ data }) {
  const heroBlockData = data.imageItemText;
  const mediaCoverageBlockData = data.mediaCoverageBlock;

  useEffect(() => {
    import("aos").then((AOS) => {
      import("aos/dist/aos.css");
      AOS.default.init({ duration: 500, easing: "ease-in", once: true, delay: 25 });
    });
  }, []);
  return (
    <>
      <Head>
        <title>Media Coverage | Red Ball Drills</title>
        <meta name="description" content="Red Ball Drills in the media." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Hero data={heroBlockData} />
      <Articles data={mediaCoverageBlockData} />
    </>
  );
}

function Hero({ data }) {
  const { title, description, mainImage } = data;
  return (
    <SectionLayout className="mb-6 md:mb-6 xl:mb-6">
      <article className="flex flex-col md:flex-row gap-12">
        <div className="md:flex-1">
          <Heading
            as="h1"
            size="page"
            className={description || mainImage ? "mb-12" : ""}
            data-aos="fade-in"
          >
            {title}
          </Heading>
          {description && <Text className="mb-7">{description}</Text>}
        </div>
        {mainImage && (
          <div className="mx-auto md:mx-0 md:flex-1">
            <Image
              src={urlForImage(mainImage).url()}
              alt={mainImage.caption}
              width={516}
              height={432}
              priority={true}
            />
          </div>
        )}
      </article>
    </SectionLayout>
  );
}

function Articles({ data }) {
  const { articles } = data;
  return (
    <SectionLayout className="mt-6 md:mt-6 xl:mt-6">
      <div className="flex justify-center min-[1224px]:justify-start flex-wrap gap-6">
        {articles.map((article, i) => (
          <Article key={i} article={article.article}>
            {article.article.title}
          </Article>
        ))}
      </div>
    </SectionLayout>
  );
}

function Article({ children, article, ...props }) {
  return (
    <Link
      href={article.link}
      target="_blank"
      rel="noopener noreferrer"
      className="max-w-[530px] md:max-w-[570px] w-full flex flex-col justify-between p-6 md:p-11 shadow-surround rounded-xl bg-rbd-base"
      {...props}
    >
      <article>
        <Heading size="video-card">{children}</Heading>
        <Text size="date" className="mt-2">
          {article.source}
        </Text>
        <Text size="date" className="mb-2">
          {article.date}
        </Text>
        <div className="relative rounded-xl overflow-hidden aspect-[10/7] w-full">
          <Image
            src={urlForImage(article.image).url()}
            fill={true}
            alt={children}
            priority={true}
            className="object-cover"
          />
        </div>
      </article>
      <span className="sr-only">(opens in new tab)</span>
    </Link>
  );
}
